import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Shield, Users, FileText, BarChart3, Activity,
  Crown, Search, ChevronLeft, ChevronRight, Trash2,
  Edit2, CheckCircle2, AlertTriangle, RefreshCw,
  Zap, TrendingUp, Calendar, Star, Eye, LogOut,
  ArrowLeft, Settings, Home, PieChart, Ticket, Send, MessageCircle,
  Clock, Brain
} from 'lucide-react';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import AdminActivityMetrics from '../components/admin/AdminActivityMetrics';
import { AdminFidemBar } from '../components/fidem/AdminFidemBar';
import { FidemMemory } from '../components/fidem/FidemMemory';
import { AdminStrategyReports } from '../components/admin/AdminStrategyReports';

// ============ STAT CARD ============
const StatCard = ({ label, value, sub, icon, color }) => (
  <div className="bg-card border border-border rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center`}>
        {icon}
      </div>
    </div>
    <p className="text-3xl font-bold font-['Outfit']">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

// ============ SUBSCRIPTION BADGE ============
const SubBadge = ({ status }) => {
  const map = {
    premium_annual: { label: 'Premium Anual', cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    premium_monthly: { label: 'Premium Mensual', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    free: { label: 'Gratuito', cls: 'bg-slate-700 text-slate-400 border-slate-600' },
  };
  const s = map[status] || map.free;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
  );
};

// ============ MAIN ADMIN PAGE ============
const AdminPage = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activatingPremium, setActivatingPremium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Support state
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportStats, setSupportStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => { checkAdmin(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab === 'overview') fetchStats();
    else if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'contracts') fetchContracts();
    else if (activeTab === 'activity') fetchActivity();
    else if (activeTab === 'support') fetchSupportData();
  }, [activeTab, isAdmin, page]);

  const checkAdmin = async () => {
    try {
      const res = await api.get('/admin/check');
      if (!res.data.is_admin) { navigate('/dashboard'); return; }
      setIsAdmin(true);
      setLoading(false);
    } catch {
      navigate('/dashboard');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (e) { toast.error('Error cargando estadísticas'); }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get(`/admin/users?page=${page}&limit=15&search=${search}`);
      setUsers(res.data.users);
      setTotalPages(res.data.pages);
      setTotalUsers(res.data.total);
    } catch (e) { toast.error('Error cargando usuarios'); }
  };

  const fetchContracts = async () => {
    try {
      const res = await api.get(`/admin/contracts?page=${page}&limit=15`);
      setContracts(res.data.contracts);
      setTotalPages(res.data.pages);
    } catch (e) { toast.error('Error cargando contratos'); }
  };

  const fetchActivity = async () => {
    try {
      const res = await api.get('/admin/activity');
      setActivity(res.data);
    } catch (e) { toast.error('Error cargando actividad'); }
  };

  const fetchSupportData = async () => {
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        api.get('/admin/support/tickets'),
        api.get('/admin/support/stats')
      ]);
      setSupportTickets(ticketsRes.data.tickets);
      setSupportStats(statsRes.data);
    } catch (e) { toast.error('Error cargando datos de soporte'); }
  };

  const handleTicketStatusChange = async (ticketId, status) => {
    try {
      await api.put(`/admin/support/tickets/${ticketId}/status`, { status });
      toast.success('Estado actualizado');
      fetchSupportData();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status });
      }
    } catch { toast.error('Error actualizando estado'); }
  };

  const handleAdminReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSendingReply(true);
    try {
      await api.post(`/admin/support/tickets/${selectedTicket.id}/reply`, { message: replyMessage });
      toast.success('Respuesta enviada');
      setReplyMessage('');
      // Refresh ticket
      const res = await api.get('/admin/support/tickets');
      const updated = res.data.tickets.find(t => t.id === selectedTicket.id);
      if (updated) setSelectedTicket(updated);
      fetchSupportData();
    } catch { toast.error('Error enviando respuesta'); }
    finally { setSendingReply(false); }
  };

  const handleActivatePremium = async () => {
    setActivatingPremium(true);
    try {
      await api.post('/admin/activate-premium-for-me');
      toast.success('✅ ¡Premium ilimitado activado! Recarga la página.');
      setTimeout(() => window.location.reload(), 1500);
    } catch { toast.error('Error activando premium'); }
    finally { setActivatingPremium(false); }
  };

  const handleUpdateSubscription = async (userId, newStatus) => {
    try {
      await api.put(`/admin/users/${userId}/subscription`, { subscription_status: newStatus });
      toast.success(`Suscripción actualizada a ${newStatus}`);
      fetchUsers();
    } catch { toast.error('Error actualizando suscripción'); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`¿Eliminar usuario ${email}? Esto borrará todos sus contratos.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error eliminando usuario'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verificando acceso de administrador...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: Home },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'metrics', label: 'Métricas', icon: Clock },
    { id: 'support', label: 'Soporte', icon: Ticket },
    { id: 'strategies', label: 'Estrategias de casos', icon: FileText },
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'contracts', label: 'Contratos', icon: FileText },
    { id: 'activity', label: 'Actividad', icon: Activity },
    { id: 'fidem', label: 'Memoria de Fidem', icon: Brain },
    { id: 'settings', label: 'Mi Cuenta', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-sm">Panel de Administrador</h1>
                <p className="text-xs text-slate-400">NotaryOS</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">{user?.email}</span>
            <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded-full flex items-center gap-1">
              <Shield className="w-3 h-3" /> Admin
            </span>
          </div>
        </div>
      </header>
      <AdminFidemBar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs - Responsive with proper spacing */}
        <div className="bg-slate-800/50 p-1.5 rounded-xl mb-6 overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-max">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`admin-tab-${tab.id}`}
                  onClick={() => { setActiveTab(tab.id); setPage(1); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===== OVERVIEW ===== */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-2xl p-6">
              <h2 className="font-bold text-lg mb-1 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" /> Acciones Rápidas
              </h2>
              <p className="text-sm text-slate-400 mb-4">Como administrador tienes acceso total a todas las funcionalidades</p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleActivatePremium}
                  disabled={activatingPremium}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                >
                  {activatingPremium ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  Activar Premium Ilimitado para Mi
                </button>
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 transition-colors text-sm"
                >
                  <Eye className="w-4 h-4" /> Ver como usuario
                </Link>
              </div>
            </div>

            {/* Stats Grid */}
            {stats ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total Usuarios" value={stats.users.total} sub={`+${stats.users.new_this_week} esta semana`} icon={<Users className="w-4 h-4 text-white" />} color="bg-blue-500/20" />
                  <StatCard label="Premium" value={stats.users.premium} sub={`${stats.users.free} gratuitos`} icon={<Crown className="w-4 h-4 text-yellow-400" />} color="bg-yellow-500/20" />
                  <StatCard label="Total Contratos" value={stats.contracts.total} sub={`+${stats.contracts.new_this_week} esta semana`} icon={<FileText className="w-4 h-4 text-white" />} color="bg-purple-500/20" />
                  <StatCard label="Contratos Firmados" value={stats.contracts.signed} sub={`${stats.contracts.drafts} borradores`} icon={<CheckCircle2 className="w-4 h-4 text-white" />} color="bg-emerald-500/20" />
                </div>

                {/* Top Contract Types */}
                <div className="bg-card border border-border rounded-xl p-5">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-400" /> Tipos de Contratos Más Usados
                  </h3>
                  <div className="space-y-2">
                    {stats.top_contract_types.map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-4">{i + 1}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm capitalize">{item.type}</span>
                            <span className="text-xs text-slate-400">{item.count}</span>
                          </div>
                          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                              style={{ width: `${Math.min(100, (item.count / (stats.contracts.total || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
              </div>
            )}
          </div>
        )}

        {/* ===== ANALYTICS ===== */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard />
        )}

        {/* ===== METRICS ===== */}
        {activeTab === 'metrics' && (
          <AdminActivityMetrics />
        )}

        {/* ===== SUPPORT ===== */}
        {activeTab === 'strategies' && <AdminStrategyReports />}

        {activeTab === 'support' && (
          <div className="space-y-6">
            {/* Support Stats */}
            {supportStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <p className="text-sm text-slate-400">Total Tickets</p>
                  <p className="text-2xl font-bold">{supportStats.total_tickets}</p>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                  <p className="text-sm text-yellow-400">Abiertos</p>
                  <p className="text-2xl font-bold text-yellow-400">{supportStats.open}</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <p className="text-sm text-blue-400">En Proceso</p>
                  <p className="text-2xl font-bold text-blue-400">{supportStats.in_progress}</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <p className="text-sm text-green-400">Resueltos</p>
                  <p className="text-2xl font-bold text-green-400">{supportStats.resolved}</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <p className="text-sm text-slate-400">Tiempo Promedio</p>
                  <p className="text-2xl font-bold">{supportStats.avg_resolution_hours}h</p>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Tickets List */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <h3 className="font-semibold mb-4 flex items-center space-x-2">
                  <Ticket className="w-5 h-5 text-cyan-400" />
                  <span>Tickets de Soporte</span>
                </h3>
                
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {supportTickets.length === 0 ? (
                    <p className="text-center py-8 text-slate-500">No hay tickets</p>
                  ) : (
                    supportTickets.map(ticket => (
                      <button
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedTicket?.id === ticket.id
                            ? 'bg-blue-600/20 border border-blue-500/30'
                            : 'bg-slate-900/50 hover:bg-slate-700/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            ticket.status === 'open' ? 'bg-yellow-500/20 text-yellow-300' :
                            ticket.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300' :
                            ticket.status === 'resolved' ? 'bg-green-500/20 text-green-300' :
                            'bg-slate-500/20 text-slate-300'
                          }`}>
                            {ticket.status === 'open' ? 'Abierto' :
                             ticket.status === 'in_progress' ? 'En Proceso' :
                             ticket.status === 'resolved' ? 'Resuelto' : 'Cerrado'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(ticket.created_at).toLocaleDateString('es-CL')}
                          </span>
                        </div>
                        <p className="font-medium text-sm truncate">{ticket.subject}</p>
                        <p className="text-xs text-slate-400 truncate">{ticket.user_email}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Ticket Detail */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                {selectedTicket ? (
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{selectedTicket.subject}</h3>
                        <p className="text-sm text-slate-400">{selectedTicket.user_email}</p>
                      </div>
                      <select
                        value={selectedTicket.status}
                        onChange={(e) => handleTicketStatusChange(selectedTicket.id, e.target.value)}
                        className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-sm"
                      >
                        <option value="open">Abierto</option>
                        <option value="in_progress">En Proceso</option>
                        <option value="resolved">Resuelto</option>
                        <option value="closed">Cerrado</option>
                      </select>
                    </div>

                    {/* Messages */}
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {selectedTicket.messages?.map(msg => (
                        <div
                          key={msg.id}
                          className={`p-3 rounded-lg ${
                            msg.sender === 'admin'
                              ? 'bg-blue-500/10 border border-blue-500/20 ml-4'
                              : 'bg-slate-900/50 mr-4'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-medium ${
                              msg.sender === 'admin' ? 'text-blue-400' : 'text-slate-300'
                            }`}>
                              {msg.sender === 'admin' ? 'Soporte' : 'Usuario'}
                            </span>
                            <span className="text-xs text-slate-500">
                              {new Date(msg.timestamp).toLocaleString('es-CL')}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-line">{msg.message}</p>
                        </div>
                      ))}
                    </div>

                    {/* Reply */}
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Escribe tu respuesta..."
                        className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && handleAdminReply()}
                      />
                      <button
                        onClick={handleAdminReply}
                        disabled={sendingReply || !replyMessage.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                    <MessageCircle className="w-12 h-12 mb-2" />
                    <p>Selecciona un ticket para ver detalles</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== USERS ===== */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">{totalUsers} Usuarios Registrados</h2>
              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar por email o nombre..."
                  className="bg-slate-800 border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500/50 text-white placeholder-slate-500 w-64"
                />
                <button type="submit" className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
                  <Search className="w-4 h-4" />
                </button>
              </form>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs text-slate-400 px-4 py-3">Usuario</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3 hidden md:table-cell">Registro</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3">Plan</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3 hidden md:table-cell">Contratos</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium">{u.name || 'Sin nombre'}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-slate-400">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('es-CL') : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <SubBadge status={u.subscription_status || 'free'} />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm">{u.contract_count || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={u.subscription_status || 'free'}
                            onChange={e => handleUpdateSubscription(u.id, e.target.value)}
                            className="text-xs bg-slate-800 border border-border rounded px-2 py-1 focus:outline-none focus:border-blue-500/50 text-white"
                          >
                            <option value="free">Gratuito</option>
                            <option value="premium_monthly">Premium Mensual</option>
                            <option value="premium_annual">Premium Anual</option>
                          </select>
                          {u.email !== user?.email && (
                            <button
                              onClick={() => handleDeleteUser(u.id, u.email)}
                              className="text-slate-500 hover:text-red-400 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-center text-slate-400 py-8 text-sm">No hay usuarios</p>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-slate-400">Página {page} de {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== CONTRACTS ===== */}
        {activeTab === 'contracts' && (
          <div className="space-y-4">
            <h2 className="font-semibold">Todos los Contratos de la Plataforma</h2>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs text-slate-400 px-4 py-3">Contrato</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3 hidden md:table-cell">Usuario</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3">Estado</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3 hidden md:table-cell">Riesgo</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3 hidden md:table-cell">Fecha</th>
                    <th className="text-left text-xs text-slate-400 px-4 py-3">Ver</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {contracts.map(c => (
                    <tr key={c.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{c.title}</p>
                          <p className="text-xs text-slate-500 capitalize">{c.template_type || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-xs text-slate-400">{c.owner_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          c.is_signed ? 'bg-emerald-500/20 text-emerald-400' :
                          c.status === 'borrador' ? 'bg-slate-700 text-slate-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {c.is_signed ? 'Firmado' : c.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {c.conflict_score != null ? (
                          <span className={`text-sm font-medium ${
                            c.conflict_score > 70 ? 'text-red-400' :
                            c.conflict_score > 40 ? 'text-yellow-400' : 'text-emerald-400'
                          }`}>{Math.round(c.conflict_score)}</span>
                        ) : <span className="text-slate-500">-</span>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-slate-400">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('es-CL') : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/contract/${c.id}`} className="text-blue-400 hover:text-blue-300">
                          <Eye className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contracts.length === 0 && (
                <p className="text-center text-slate-400 py-8 text-sm">No hay contratos</p>
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-slate-400">Página {page} de {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} className="p-1.5 rounded hover:bg-slate-700 disabled:opacity-30">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ===== ACTIVITY ===== */}
        {activeTab === 'activity' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Actividad Reciente de la Plataforma</h2>
              <button onClick={fetchActivity} className="p-1.5 text-slate-400 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-card border border-border rounded-xl divide-y divide-border/50">
              {activity.map((act, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Activity className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{act.detail}</p>
                    <p className="text-xs text-slate-500">{act.user_email}</p>
                  </div>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {act.timestamp ? new Date(act.timestamp).toLocaleString('es-CL') : '-'}
                  </span>
                </div>
              ))}
              {activity.length === 0 && (
                <p className="text-center text-slate-400 py-8 text-sm">Sin actividad reciente</p>
              )}
            </div>
          </div>
        )}

        {/* ===== SETTINGS ===== */}
        {activeTab === 'fidem' && <FidemMemory />}
        {activeTab === 'fidem' && <Link data-testid="admin-vocabulary-link" to="/vocabulario-juridico/calidad" className="mt-6 inline-block text-sm font-semibold text-emerald-300">Vocabulario jurídico · revisión, fuentes y calidad →</Link>}

        {activeTab === 'settings' && (
          <div className="space-y-6 max-w-lg">
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Crown className="w-4 h-4 text-yellow-400" /> Mi Cuenta Admin
              </h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-400">Email</span>
                  <span className="text-sm">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-400">Rol</span>
                  <span className="text-sm text-red-400 font-medium">Administrador Principal</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-400">Plan</span>
                  <SubBadge status={user?.subscription_status || 'free'} />
                </div>
              </div>
              <button
                onClick={handleActivatePremium}
                disabled={activatingPremium}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {activatingPremium ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                Activar Premium Ilimitado
              </button>
              <p className="text-xs text-slate-500 text-center mt-2">Acceso completo a todas las funciones sin límites</p>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold mb-3">Funcionalidades de Prueba</h3>
              <div className="space-y-2 text-sm text-slate-300">
                {[
                  '✅ Análisis de riesgo ilimitado',
                  '✅ Simplificador legal ilimitado',
                  '✅ Chat IA sin restricciones',
                  '✅ Detección de sesgo ilimitada',
                  '✅ Firmas biométricas',
                  '✅ Exportar PDF/Word',
                  '✅ Colaboración en tiempo real',
                  '✅ Smart contracts (testnet)',
                  '✅ Rastreador de vencimientos',
                  '✅ Importar contratos PDF/Word',
                ].map((f, i) => <p key={i}>{f}</p>)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
